import { AppContext } from 'context/ContextProvider';
import { Button, Dropdown } from 'components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { DisplayTextKeys, OffersType, QUERY_KEYS, SetSlideoutConfigType, SlideoutConfigType } from 'views/Trade/types';
import { useQueryClient } from '@tanstack/react-query';
import { IAcceptHighestBidBody } from 'views/Trade/services/acceptHighestBid';
import { QUERY_REFECH_TIMEOUT, STOP_LIMIT_TYPE } from 'views/Trade/constants';
import { ViewStateType } from 'components/ProductTemplates/types';
import { useFetch } from 'views/Trade/hooks/useFetch';
import { SlideoutSkeleton } from 'components/Skeleton';
import { fetchPortfolioStocks } from 'views/Trade/services/fetchPortfolioStocks';
import { buildDisplayText } from 'utils';
import { useTranslation } from 'react-i18next';
import { generateQuantityDropdown } from 'views/Trade/helpers';
import { DropdownItem } from 'components/Dropdown';
import { useHandleAcceptBid } from 'views/shared/hooks/useHandleAcceptBid';
import { PORTAL_DESCRIPTIONS } from 'types/commonTypes';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { fetchBid } from 'views/Trade/services/fetchBids';
import WineInfoTemplate from './WineInfoTemplate';

interface AcceptBidViewProps {
  slideOutConfig: SlideoutConfigType;
  setSlideOutConfig: SetSlideoutConfigType;
}

const AcceptBidView = ({ slideOutConfig, setSlideOutConfig }: AcceptBidViewProps) => {
  const {
    state: {
      settings: { accountInfo },
    },

    gbpToTargetCurrency,
  } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);

  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const { handleAcceptBid } = useHandleAcceptBid();
  const { responce: portfolioStocksResponce } = useFetch({
    queryFn: () => fetchPortfolioStocks({ search: slideOutConfig.offer?.lwin18, page: 0, pageSize: 10 }),
    queryKey: [`${QUERY_KEYS.FETCH_PORTFOLIO_STOCK}${slideOutConfig.offer?.lwin18}`],
  });

  // const { responce: bidResponce } = useFetch({
  //   queryFn: () => fetchBid({ lwin18: slideOutConfig.offer?.lwin18 }),
  //   queryKey: [`${QUERY_KEYS.FETCH_BID}${slideOutConfig.offer?.lwin18}`],
  // });

  const bidQty = useMemo(() => {
    return slideOutConfig.offer?.highestBid?.quantityOutstanding ?? 0;
  }, [slideOutConfig.offer]);

  const stock = useMemo(() => {
    if (!portfolioStocksResponce?.data[0]) return null;
    return portfolioStocksResponce?.data[0];
  }, [portfolioStocksResponce?.data]);

  const acceptBid = async (
    offer: OffersType,
    refetchCashBalances?: () => void,
    accountId?: string,
    quantity?: number,
  ) => {
    const requestBody: IAcceptHighestBidBody = {
      description: PORTAL_DESCRIPTIONS.PORTAL_ACCEPTBID_TRADE,
      accountId: accountId || '',
      items: [
        {
          lwin18: offer.lwin18,
          stopLimitPerUnit: offer.highestBid?.price as number,
          stopLimitType: STOP_LIMIT_TYPE.customPrice,
          priceType: 'AcceptHighestBid',
          quantity: quantity ?? 0,
        },
      ],
    };

    await handleAcceptBid(requestBody, {
      success: () => {
        setSlideOutConfig((prev) => ({
          ...prev,
          view: ViewStateType.THANK_YOU,
          templateConfig: { translationKey: 'common:generalSuccess' },
          isOpen: true,
        }));
      },
      isLoading: () => {
        setSlideOutConfig((prev) => ({
          ...prev,
          view: ViewStateType.LOADING,
        }));
      },
      error: () => {
        setSlideOutConfig((prev) => ({
          ...prev,
          view: ViewStateType.ACCEPT_BID_ERROR,
          onCTA: () =>
            setSlideOutConfig(() => ({
              ...prev,
              view: ViewStateType.DEFAULT,
            })),
        }));
      },
      finally: () => {
        setTimeout(() => {
          // only works if they are separately called
          queryClient.resetQueries({
            queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS],
          });
          queryClient.resetQueries({
            queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_STOCK],
          });
          queryClient.resetQueries({
            queryKey: [QUERY_KEYS.FETCH_OFFERS],
          });

          if (slideOutConfig?.searchText) {
            queryClient.resetQueries({
              queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS_SEARCH, slideOutConfig.searchText],
            });
            queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH, slideOutConfig.searchText] });
          }

          refetchCashBalances?.();
        }, QUERY_REFECH_TIMEOUT);
      },
    });
  };

  const qtyDropdown = (generateDropdown: (quantity?: number) => DropdownItem[], stockQuantity: number) => {
    let qtyToGenerate = 0;
    if (bidQty > stockQuantity) {
      qtyToGenerate = stockQuantity;
    } else {
      qtyToGenerate = bidQty ?? 0;
    }
    return generateDropdown(qtyToGenerate);
  };
  // dd
  useEffect(() => {
    if (stock?.physicalQuantity === 1) setSelectedQuantity(1);
  }, [stock?.physicalQuantity]);

  return (
    <FeedbackTemplate
      viewState={slideOutConfig?.view || ViewStateType.DEFAULT}
      templateConfig={slideOutConfig.templateConfig}
      onCTA={slideOutConfig.onCTA}
      onClose={() => {
        setSlideOutConfig({ isOpen: false });
      }}
    >
      <WineInfoTemplate config={{ ...slideOutConfig.wineInfoTemplateConfig, unitSize: stock?.unitSize }}>
        <div className="flex flex-col flex-1 bg-white dark:bg-slate-900">
          {!stock?.assetAccountId ? (
            <SlideoutSkeleton />
          ) : (
            <>
              {/* Selection Card */}
              <div className="px-4 pt-2">
                <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4 border border-gray-100 dark:border-slate-700/50 shadow-sm dark:shadow-slate-950/30">
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      {displayText[DisplayTextKeys.PLEASE_SELECT_QUANTITY]}
                    </span>
                    <Dropdown
                      placeholder={displayText[DisplayTextKeys.PLEASE_SELECT_QUANTITY]}
                      value={selectedQuantity > 0 ? selectedQuantity?.toString() : ''}
                      containerClassName="w-full rounded-xl"
                      itemsContainerClassName="max-h-[250px] min-h-10 overflow-y-auto bg-white dark:bg-slate-700 rounded-xl shadow-lg dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-600"
                      className="flex-1 text-14 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-slate-600 h-12 p-4 rounded-xl justify-between items-center bg-white dark:bg-slate-700 hover:border-teal-500/50 transition-colors"
                      itemsWrapperClassName="w-full"
                      items={qtyDropdown(generateQuantityDropdown, stock?.physicalQuantity ?? 0)}
                      onItemSelect={(item) => setSelectedQuantity(Number(item.value))}
                    />
                  </div>
                </div>
              </div>

              {/* Bid Info Card */}
              <div className="px-4 py-4">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 dark:from-teal-900/20 dark:to-teal-800/10 rounded-xl p-5 border border-teal-100 dark:border-teal-700/30 shadow-sm">
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-teal-600 dark:text-teal-400 uppercase tracking-wide mb-1">{`${
                      displayText[DisplayTextKeys.ACCEPT_BID_MESSAGE]
                    }`}</span>
                    <span className="text-base text-gray-900 dark:text-white font-bold">
                      {`${selectedQuantity} x ${gbpToTargetCurrency.convertAndFormat(
                        slideOutConfig?.offer?.highestBid?.price ?? 0,
                      )} `}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="px-4 pb-4 mt-auto">
                <Button
                  isDisable={!selectedQuantity}
                  onClick={() =>
                    acceptBid(
                      slideOutConfig?.offer as OffersType,
                      accountInfo?.account.refetchCashBalances,
                      stock?.assetAccountId || '',
                      selectedQuantity,
                    )
                  }
                  type="button"
                  className="w-full h-12 text-base font-semibold rounded-full btn bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 disabled:opacity-50 disabled:shadow-none transition-all"
                >
                  {displayText[DisplayTextKeys.ACCEPT_BID]}
                </Button>
              </div>
            </>
          )}
        </div>
      </WineInfoTemplate>
    </FeedbackTemplate>
  );
};

export default AcceptBidView;
